/**
 * 가격표 API 클라이언트 — backend /price-list/* 와 1:1.
 */

import type { ApiResponse, ApiResponseList } from './types';

const BASE = '/api';

/**
 * 주요항목 가격표 구분 = 대분류 4가지 · 이 순서로 화면에 묶인다 (v2.230.0 · 개발 요청 #15).
 *   ⚠서버 routes/price-list/index.ts MAIN_PRICE_CATEGORIES 와 같은 목록 — 하나를 고치면 둘 다.
 */
export const MAIN_PRICE_CATEGORIES = ['스토리지', '서버', 'SW', '기타'] as const;

/** 묶음 순서 — 4대분류 먼저, 그 밖(아직 안 바꾼 옛 구분)은 뒤에 가나다순 */
export function mainCategoryRank(c: string): number {
  const i = (MAIN_PRICE_CATEGORIES as readonly string[]).indexOf(c);
  return i >= 0 ? i : MAIN_PRICE_CATEGORIES.length;
}

export interface MainProductRow {
  id: number;
  category: string;
  name: string;
  description: string;
  retailPrice: number;
  supplyPrice: number;
  note: string | null;
  sortOrder: number;
}

export interface MainProductInput {
  category: string;
  name: string;
  description?: string;
  retailPrice: number;
  supplyPrice: number;
  note?: string | null;
  sortOrder?: number;
}

export async function listMainProducts(): Promise<MainProductRow[]> {
  const res = await fetch(`${BASE}/price-list/main`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponseList<MainProductRow>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function createMainProduct(input: MainProductInput): Promise<number> {
  const res = await fetch(`${BASE}/price-list/main`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.id;
}

/** 여러 건 일괄 등록 (견적서 항목 → 가격표 추가). category 는 항목마다 포함. */
export async function bulkCreateMainProducts(
  items: MainProductInput[],
): Promise<{ count: number; ids: number[] }> {
  const res = await fetch(`${BASE}/price-list/main/bulk`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  const json = (await res.json()) as ApiResponse<{ count: number; ids: number[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function updateMainProduct(
  id: number,
  input: Partial<MainProductInput>,
): Promise<void> {
  const res = await fetch(`${BASE}/price-list/main/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function deleteMainProduct(id: number): Promise<void> {
  const res = await fetch(`${BASE}/price-list/main/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}
